import useAmbassador from "containers/ambassador/useHooks";
import useStore from "hooks/useStore";
import React, { FC, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FiX, FiImage } from "react-icons/fi";
import { CreatePostDTO } from "utils/helpers/models/ambassador/create-post.dto";

interface NewPostProps {
  isOpen: boolean;
  handleConfirm: () => void;
  onClose: () => void;
   title?: string;
}

interface PostFormValues {
  caption: string;
  location?: string;
}

const NewPost: FC<NewPostProps> = ({ isOpen, onClose, handleConfirm ,title }) => {
  const [images, setImages] = useState<{ id: number; url: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getUserProfilePic } = useStore();
  const { uploadMultipleFiles, postNewPost } = useAmbassador();
  const userProfilePic = getUserProfilePic();


  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostFormValues>();

  const onSubmit = (data: PostFormValues) => {
    if (images.length === 0) {
      alert("Please upload at least one image.");
      return;
    }
    const mediaIds = images.map((img) => img.id);
    const newPostDto = new CreatePostDTO({
      type: title === "Photo of New Business" ? "commitment" : "post",
      caption: data.caption,
      location: data.location || "",
      mediaIds,
    });
    postNewPost(newPostDto)
    handleConfirm();
    onClose();
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    const previewImages: { id: number; url: string }[] = [];
    fileList.forEach((file) => {
      const previewUrl = URL.createObjectURL(file);
      previewImages.push({ id: 0, url: previewUrl });
    });
    setImages((prev) => [...prev, ...previewImages]);
    try {
      const uploadedFiles = await uploadMultipleFiles(fileList);
      if (uploadedFiles.length) {
        const uploadedWithPreview = uploadedFiles.map((img, index) => ({
          id: img.id,
          url: previewImages[index]?.url || "",
        }));
        setImages((prev) => [
          ...prev.filter((img) => img.id !== 0),
          ...uploadedWithPreview,
        ]);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center px-4">
      <div
        className="bg-white rounded-2xl w-full max-w-xl p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-black"
          onClick={onClose}
        >
          <FiX className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-semibold text-center mb-6">{title ? title : "New Post"}</h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex items-start mb-4">
            <img
              src={userProfilePic}
              alt="Profile"
              className="w-10 h-10 rounded-full mr-3 object-cover"
            />
            <textarea
              {...register("caption", { required: "Caption is required" })}
              placeholder="What do you want to talk about?"
              rows={1}
              className="text-gray-800 w-full resize-none overflow-auto focus:outline-none bg-transparent max-h-40"
              onInput={(e) => {
                e.currentTarget.style.height = "auto";
                e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
              }}
            />
          </div>

          {errors.caption && (
            <p className="text-sm text-red-500 mb-2">{errors.caption.message}</p>
          )}

          <div className="flex space-x-3 mb-6 overflow-x-auto py-5">
            {images.map((img, index) => (
              <div key={index} className="relative w-20 h-20 flex-shrink-0">
                <img
                  src={img.url}
                  alt={`preview-${index}`}
                  className="w-full h-full object-cover rounded-xl"
                />
                <button
                  type="button"
                  className="absolute -top-2 -right-2 bg-white rounded-full p-[2px] shadow-md"
                  onClick={() => removeImage(index)}
                >
                  <FiX className="text-red-500 text-xs" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center space-x-4 mb-4">
            <FiImage
              className="w-5 h-5 text-gray-500 cursor-pointer"
              onClick={handleImageClick}
            />
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>

          <button
            type="submit"
            className="w-full bg-[#233746] text-white rounded-full py-3 font-medium hover:bg-opacity-90 transition"
          >
            Share
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewPost;
